import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Truck, ClipboardList, LogOut, Banknote, Users, AlertCircle, Newspaper } from 'lucide-react';
import LogoMark from './LogoMark';
import { vendorApi } from '../services/api';

interface SidebarProps {
  logoUrl?: string;
  companyName?: string;
  onLogout?: () => void;
}

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Översikt' },
  { path: '/settlement-orders', icon: ClipboardList, label: 'Transaktioner' },
  { path: '/fixed-costs', icon: Banknote, label: 'Fasta kostnader' },
  { path: '/open-expenses', icon: AlertCircle, label: 'Utgifter och avdrag' },
  { path: '/invoices', icon: FileText, label: 'Avräkningar' },
  { path: '/customers', icon: Users, label: 'Kunder' },
  { path: '/resources', icon: Truck, label: 'Resurser' },
  { path: '/news', icon: Newspaper, label: 'Nyheter' },
];

export default function Sidebar({ logoUrl, companyName, onLogout }: SidebarProps) {
  const navigate = useNavigate();

  // Every page gets a working "Logga ut"; pages may still pass their own handler
  const logout =
    onLogout ??
    (async () => {
      try {
        await vendorApi.logout();
      } finally {
        navigate('/login');
      }
    });

  return (
    <aside className="card sticky top-3 m-3 mr-0 flex h-[calc(100dvh-1.5rem)] w-64 shrink-0 flex-col">
      {/* The tenant's colour, kept to accents: this line and the active menu icon */}
      <div aria-hidden className="h-[3px] shrink-0 bg-primary" />

      {/* Tenant identity */}
      <div className="px-6 pb-6 pt-8">
        {logoUrl ? (
          <img src={logoUrl} alt={companyName || 'Företagets logotyp'} className="h-12 w-auto object-contain" />
        ) : (
          <p className="font-display text-[0.95rem] leading-snug text-ink text-balance">
            {companyName || 'Leverantörsportal'}
          </p>
        )}
        <p className="mt-3 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-brass">
          <span aria-hidden className="bg-grad-line h-[2px] w-6" />
          Leverantörsportal
        </p>
      </div>

      {/* Navigation */}
      <nav aria-label="Huvudmeny" className="flex-1 overflow-y-auto px-3">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-full px-4 py-2.5 text-[14px] font-medium transition-all duration-300 ease-fluid active:scale-[0.98] ${
                      isActive
                        ? 'bg-[rgb(6_26_48_/_0.05)] text-ink ring-1 ring-inset ring-[rgb(6_26_48_/_0.07)]'
                        : 'text-gray-600 hover:bg-[rgb(6_26_48_/_0.035)] hover:text-ink'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={`h-[18px] w-[18px] shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-gray-400 group-hover:text-primary'}`}
                        strokeWidth={1.75}
                        aria-hidden
                      />
                      {item.label}
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Log out + provider */}
      <div className="mx-3 border-t border-gray-100 py-3">
        {(
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-[14px] font-medium text-gray-600 transition-all duration-300 ease-fluid hover:bg-gray-100 hover:text-ink active:scale-[0.98]"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
            Logga ut
          </button>
        )}
        <p className="mt-3 flex items-center gap-2 px-4 pb-1 text-[11px] text-gray-500">
          <LogoMark className="h-4 w-auto text-gray-500" />
          Settled av Flank Speed
        </p>
      </div>
    </aside>
  );
}
